/**
 * Mermaid Code Sanitizer and Validator
 *
 * Provides robust error handling for LLM-generated Mermaid diagrams.
 * Auto-fixes common syntax issues before rendering.
 */

import mermaid from 'mermaid';

export interface SanitizeResult {
    code: string;
    wasFixed: boolean;
    fixesApplied: string[];
}

export interface ValidationResult {
    valid: boolean;
    error?: string;
    sanitizedCode?: string;
    fixesApplied?: string[];
}

/**
 * Common diagram type patterns that Mermaid supports
 */
const DIAGRAM_TYPES = [
    'flowchart',
    'graph',
    'sequenceDiagram',
    'classDiagram',
    'stateDiagram',
    'stateDiagram-v2',
    'erDiagram',
    'journey',
    'gantt',
    'pie',
    'quadrantChart',
    'requirementDiagram',
    'gitGraph',
    'mindmap',
    'timeline',
    'zenuml',
    'sankey-beta',
    'xychart-beta',
    'block-beta',
];

/**
 * Sanitize and fix common LLM-generated Mermaid code issues
 */
export function sanitizeMermaidCode(code: string): SanitizeResult {
    const fixesApplied: string[] = [];
    let sanitized = code;

    // 1. Decode HTML entities (LLMs often escape these)
    const htmlEntityFixes: [RegExp, string, string][] = [
        [/&gt;/g, '>', 'HTML entity &gt; → >'],
        [/&lt;/g, '<', 'HTML entity &lt; → <'],
        [/&amp;/g, '&', 'HTML entity &amp; → &'],
        [/&quot;/g, '"', 'HTML entity &quot; → "'],
        [/&#39;/g, "'", "HTML entity &#39; → '"],
        [/&nbsp;/g, ' ', 'HTML entity &nbsp; → space'],
        [/&#x27;/g, "'", "HTML entity &#x27; → '"],
        [/&#x22;/g, '"', 'HTML entity &#x22; → "'],
    ];

    for (const [pattern, replacement, description] of htmlEntityFixes) {
        if (pattern.test(sanitized)) {
            sanitized = sanitized.replace(pattern, replacement);
            fixesApplied.push(description);
        }
    }

    // 2. Remove markdown code fence markers if present
    const codeBlockMatch = sanitized.match(/^```(?:mermaid)?\s*\n?([\s\S]*?)\n?```$/);
    if (codeBlockMatch) {
        sanitized = codeBlockMatch[1];
        fixesApplied.push('Removed markdown code fence');
    }

    // 3. Trim whitespace and normalize line endings
    sanitized = sanitized.trim().replace(/\r\n/g, '\n');

    // 4. Remove excessive blank lines (more than 2 consecutive)
    const beforeBlankLines = sanitized;
    sanitized = sanitized.replace(/\n{3,}/g, '\n\n');
    if (beforeBlankLines !== sanitized) {
        fixesApplied.push('Removed excessive blank lines');
    }

    // 5. Check if diagram type declaration exists, try to infer if missing
    const hasValidDiagramType = DIAGRAM_TYPES.some(type => {
        const pattern = new RegExp(`^\\s*${type}`, 'im');
        return pattern.test(sanitized);
    });

    if (!hasValidDiagramType) {
        // Try to infer the diagram type from content
        const inferredType = inferDiagramType(sanitized);
        if (inferredType) {
            sanitized = `${inferredType}\n${sanitized}`;
            fixesApplied.push(`Added missing diagram type: ${inferredType}`);
        }
    }

    // 6. Fix common arrow syntax issues
    // LLMs sometimes use -> instead of --> for flowcharts
    if (/^(flowchart|graph)/im.test(sanitized)) {
        // Fix single dash arrows to double dash (but not in labels)
        const beforeArrows = sanitized;
        // Match arrows not inside quotes/brackets
        sanitized = sanitized.replace(/(\w+|\]|\))\s*->\s*(\w+|\[|\()/g, '$1 --> $2');
        sanitized = sanitized.replace(/(\w+|\]|\))\s*<-\s*(\w+|\[|\()/g, '$1 <-- $2');
        if (beforeArrows !== sanitized) {
            fixesApplied.push('Fixed arrow syntax (-> to -->)');
        }
    }

    // 7. Fix unbalanced quotes in node labels
    sanitized = fixUnbalancedQuotes(sanitized, fixesApplied);

    // 8. Fix invalid node IDs (spaces, special characters)
    sanitized = fixInvalidNodeIds(sanitized, fixesApplied);

    // 9. Fix common sequence diagram issues
    if (/^sequenceDiagram/im.test(sanitized)) {
        sanitized = fixSequenceDiagramIssues(sanitized, fixesApplied);
    }

    // 10. Fix common class diagram issues
    if (/^classDiagram/im.test(sanitized)) {
        sanitized = fixClassDiagramIssues(sanitized, fixesApplied);
    }

    // 11. Fix common ER diagram issues
    if (/^erDiagram/im.test(sanitized)) {
        sanitized = fixErDiagramIssues(sanitized, fixesApplied);
    }

    // 13. Remove any stray HTML tags that might have leaked through
    const beforeHtmlClean = sanitized;
    sanitized = sanitized.replace(/<\/?(?:div|span|p|br|pre|code)[^>]*>/gi, '');
    if (beforeHtmlClean !== sanitized) {
        fixesApplied.push('Removed stray HTML tags');
    }

    // 14. Fix direction declarations (LR, RL, TB, BT)
    if (/^(flowchart|graph)/im.test(sanitized)) {
        // Ensure direction is on the same line as flowchart/graph
        sanitized = sanitized.replace(/^(flowchart|graph)\s*\n\s*(LR|RL|TB|BT|TD)\s*$/im, '$1 $2');
    }

    return {
        code: sanitized.trim(),
        wasFixed: fixesApplied.length > 0,
        fixesApplied,
    };
}

/**
 * Attempt to infer diagram type from content
 */
function inferDiagramType(code: string): string | null {
    const lines = code.split('\n').map(l => l.trim()).filter(l => l);

    // Check for sequence diagram patterns
    if (code.includes('->>') || code.includes('-->>') || /participant\s+\w+/i.test(code)) {
        return 'sequenceDiagram';
    }

    // Check for class diagram patterns
    if (/class\s+\w+\s*\{/i.test(code) || code.includes('<|--') || code.includes('--|>')) {
        return 'classDiagram';
    }

    // Check for ER diagram patterns
    if (code.includes('||--') || code.includes('--||') || code.includes('}|')) {
        return 'erDiagram';
    }

    // Check for state diagram patterns
    if (/\[\*\]\s*-->/.test(code) || /-->\s*\[\*\]/.test(code)) {
        return 'stateDiagram-v2';
    }

    // Check for gantt chart patterns
    if (/section\s+\w+/i.test(code) && /:\s*\w+,\s*\d{4}-\d{2}-\d{2}/.test(code)) {
        return 'gantt';
    }

    // Check for pie chart patterns
    if (/"\w+"\s*:\s*\d+/.test(code) && lines.length < 15) {
        return 'pie';
    }

    // Check for journey patterns
    if (/section\s+\w+/i.test(code) && /:\s*\d+:/.test(code)) {
        return 'journey';
    }

    // Check for mindmap patterns
    if (/^\s+\w+/.test(code) && !code.includes('-->') && !code.includes('->')) {
        return 'mindmap';
    }

    // Default to flowchart for arrow-based content
    if (code.includes('-->') || code.includes('---') || code.includes('-.->')) {
        return 'flowchart TD';
    }

    // Check for node definitions with brackets
    if (/\w+\[.*\]/.test(code) || /\w+\(.*\)/.test(code)) {
        return 'flowchart TD';
    }

    return null;
}

/**
 * Fix unbalanced quotes in node labels
 */
function fixUnbalancedQuotes(code: string, fixes: string[]): string {
    const lines = code.split('\n');
    let fixed = false;

    const fixedLines = lines.map(line => {
        // Count quotes
        const doubleQuotes = (line.match(/"/g) || []).length;
        const singleQuotes = (line.match(/'/g) || []).length;

        // Fix unbalanced double quotes
        if (doubleQuotes % 2 !== 0) {
            // Find the last unbalanced quote and close it
            const lastQuoteIndex = line.lastIndexOf('"');
            if (lastQuoteIndex !== -1) {
                // Check if it's an opening quote that needs closing
                const beforeQuote = line.substring(0, lastQuoteIndex);
                const afterQuote = line.substring(lastQuoteIndex + 1);
                if ((beforeQuote.match(/"/g) || []).length % 2 === 0) {
                    // This is an opening quote, add closing at end of content
                    line = line + '"';
                    fixed = true;
                }
            }
        }

        return line;
    });

    if (fixed) {
        fixes.push('Fixed unbalanced quotes');
    }

    return fixedLines.join('\n');
}

/**
 * Fix invalid node IDs in flowcharts
 */
function fixInvalidNodeIds(code: string, fixes: string[]): string {
    if (!/^(flowchart|graph)/im.test(code)) {
        return code;
    }

    let fixed = false;
    const lines = code.split('\n');

    const fixedLines = lines.map(line => {
        // Skip diagram declaration and comments
        if (/^(flowchart|graph|%%)/i.test(line.trim())) {
            return line;
        }

        // Fix node IDs with spaces by replacing with underscores
        // Match: "Node Name" --> B  (should be Node_Name --> B)
        const nodeIdPattern = /^(\s*)(\w+(?:\s+\w+)+)(\s*(?:-->|---|\.-+>|=+>))/;
        const match = line.match(nodeIdPattern);
        if (match) {
            const [, indent, nodeId, arrow] = match;
            const fixedNodeId = nodeId.replace(/\s+/g, '_');
            line = line.replace(nodeIdPattern, `${indent}${fixedNodeId}${arrow}`);
            fixed = true;
        }

        return line;
    });

    if (fixed) {
        fixes.push('Fixed invalid node IDs (spaces → underscores)');
    }

    return fixedLines.join('\n');
}

/**
 * Fix common sequence diagram issues
 */
function fixSequenceDiagramIssues(code: string, fixes: string[]): string {
    let sanitized = code;
    let fixed = false;

    // Fix participant names with spaces (need quotes)
    const participantPattern = /^(\s*participant\s+)([^"\n]+?\s+[^"\n]+?)(\s*(?:as\s+\w+)?\s*)$/gm;
    sanitized = sanitized.replace(participantPattern, (match, prefix, name, suffix) => {
        if (!name.startsWith('"')) {
            fixed = true;
            return `${prefix}"${name.trim()}"${suffix}`;
        }
        return match;
    });

    // Fix message syntax: A->B should be A->>B or A-->>B
    const singleArrowPattern = /(\w+)\s*->\s*(\w+)\s*:/g;
    if (singleArrowPattern.test(sanitized)) {
        sanitized = sanitized.replace(singleArrowPattern, '$1->>$2:');
        fixed = true;
    }

    if (fixed) {
        fixes.push('Fixed sequence diagram syntax');
    }

    return sanitized;
}

/**
 * Fix common class diagram issues
 */
function fixClassDiagramIssues(code: string, fixes: string[]): string {
    let sanitized = code;
    let fixed = false;

    // Fix class definitions without proper syntax
    // class ClassName { should be class ClassName {
    const classPattern = /^(\s*)class\s+(\w+)\s*\{/gm;
    const beforeClass = sanitized;
    sanitized = sanitized.replace(classPattern, '$1class $2 {');
    if (beforeClass !== sanitized) {
        fixed = true;
    }

    // Fix method signatures missing parentheses
    // +methodName should be +methodName()
    const methodPattern = /^(\s*[+\-#~])(\w+)(\s*)$/gm;
    sanitized = sanitized.replace(methodPattern, '$1$2()$3');
    if (sanitized !== beforeClass) {
        fixed = true;
    }

    if (fixed) {
        fixes.push('Fixed class diagram syntax');
    }

    return sanitized;
}

/**
 * Fix common ER diagram issues from LLM output
 * Handles: lost newlines, invalid cardinality syntax, undefined entities, spacing
 */
function fixErDiagramIssues(code: string, fixes: string[]): string {
    let sanitized = code;

    // --- Fix 1: Reconstruct newlines if they were lost (HTML attribute normalization) ---
    const lines = sanitized.split('\n').filter(l => l.trim());
    const contentAfterHeader = lines.slice(1).join('\n');

    // Detect if newlines were lost: multiple { on one line, or entity names run together
    const braceCount = (contentAfterHeader.match(/\{/g) || []).length;
    const lineCount = lines.length - 1; // exclude header
    const looksFlattened = braceCount > 1 && lineCount < braceCount * 2;

    if (looksFlattened) {
        console.log('[Mermaid Sanitizer] erDiagram appears to have lost newlines, reconstructing...');

        // Work with the full text after "erDiagram"
        const headerMatch = sanitized.match(/^(\s*erDiagram\s*)/i);
        const header = headerMatch ? headerMatch[1] : 'erDiagram\n';
        let body = sanitized.substring(header.length);

        // Insert newlines before entity names followed by {
        body = body.replace(/\s*\b([A-Z_][A-Z0-9_]*)\s*\{/g, '\n$1 {');

        // Insert newlines before closing braces
        body = body.replace(/\s*\}/g, '\n}');

        // Insert newlines between field definitions inside entities
        // Fields look like: type fieldname [PK|FK|UK]
        const fieldTypes = 'string|int|integer|float|double|boolean|date|datetime|timestamp|text|varchar|uuid|bigint|decimal|numeric';
        const fieldTypeRegex = new RegExp(`(\\w+(?:\\s+(?:PK|FK|UK))?)\\s+((?:${fieldTypes})\\s+\\w+)`, 'gi');
        body = body.replace(fieldTypeRegex, (match, prev, next) => {
            // Check if prev ends with a known field type (meaning prev IS a field name, not end of another field)
            const prevTrimmed = prev.trim();
            const typesList = fieldTypes.split('|');
            if (typesList.includes(prevTrimmed.toLowerCase())) {
                return match; // Don't split - we're mid-field
            }
            return `${prev}\n    ${next}`;
        });

        // Insert newlines before relationship lines
        // Relationships: ENTITY1 cardinality--cardinality ENTITY2 : label
        body = body.replace(/\}\s*\n?\s*([A-Z_][A-Z0-9_]*\s*[|}])/g, '}\n$1');

        sanitized = header.trimEnd() + '\n' + body.trim();
        fixes.push('Reconstructed newlines in erDiagram');
    }

    // --- Fix 2: Fix invalid cardinality operators (line-by-line to avoid entity body corruption) ---
    // LLMs often write }--|| instead of }|--|| (missing | or o after } in relationships)
    const cardLines = sanitized.split('\n');
    let cardFixed = false;
    const fixedCardLines = cardLines.map(line => {
        const trimmed = line.trim();
        // Only fix lines that look like relationship lines (have -- and : for label)
        if (!trimmed.includes('--') || !trimmed.includes(':')) return line;
        // Must have entity names on both sides
        if (!/[A-Z_][A-Z0-9_]*.*--.*[A-Z_][A-Z0-9_]*\s*:/.test(trimmed)) return line;

        let fixed = line;
        // }-- should be }|-- (one or more) - common LLM mistake
        fixed = fixed.replace(/\}--/g, '}|--');
        // --} should be --|} which isn't standard, likely meant --||
        fixed = fixed.replace(/--\}(?![|o])/g, '--||');

        if (fixed !== line) {
            cardFixed = true;
        }
        return fixed;
    });
    if (cardFixed) {
        sanitized = fixedCardLines.join('\n');
        fixes.push('Fixed invalid ER diagram cardinality operators (}-- → }|--)');
    }

    // --- Fix 3: Ensure proper spacing around relationship operators ---
    const spacingLines = sanitized.split('\n');
    let spacingFixed = false;
    const fixedSpacingLines = spacingLines.map(line => {
        const trimmed = line.trim();
        // Only fix relationship lines
        if (!trimmed.includes('--') || !trimmed.includes(':')) return line;
        if (!/[A-Z_][A-Z0-9_]*/.test(trimmed)) return line;

        let fixed = line;
        // Add space between word and relationship operator
        fixed = fixed.replace(/(\w)(\|\|--|\}[|o]--|o[|{]--)/g, '$1 $2');
        // Add space between relationship operator and word
        fixed = fixed.replace(/(--\|\||--[|o]\{|--[|o]\||--\}[|o])\s*(\w)/g, '$1 $2');

        if (fixed !== line) {
            spacingFixed = true;
        }
        return fixed;
    });
    if (spacingFixed) {
        sanitized = fixedSpacingLines.join('\n');
        fixes.push('Fixed ER diagram relationship spacing');
    }

    // --- Fix 4: Auto-generate stub entities for undefined references ---
    // Collect defined entity names
    const definedEntities = new Set<string>();
    const entityDefRegex = /^\s*([A-Z_][A-Z0-9_]*)\s*\{/gm;
    let entityMatch;
    while ((entityMatch = entityDefRegex.exec(sanitized)) !== null) {
        definedEntities.add(entityMatch[1]);
    }

    // Collect referenced entity names from relationships
    const referencedEntities = new Set<string>();
    // Match relationship lines: ENTITY1 <cardinality>--<cardinality> ENTITY2 : label
    const relationshipRegex = /^\s*([A-Z_][A-Z0-9_]*)\s+[|{}o]+--[|{}o]+\s+([A-Z_][A-Z0-9_]*)\s*:/gm;
    let relMatch;
    while ((relMatch = relationshipRegex.exec(sanitized)) !== null) {
        referencedEntities.add(relMatch[1]);
        referencedEntities.add(relMatch[2]);
    }

    // Find undefined entities and add stub definitions
    const undefinedEntities: string[] = [];
    referencedEntities.forEach(entity => {
        if (!definedEntities.has(entity)) {
            undefinedEntities.push(entity);
        }
    });

    if (undefinedEntities.length > 0) {
        // Insert stub entity definitions before the first relationship line
        const stubs = undefinedEntities.map(e => `${e} {\n    string id PK\n}`).join('\n');
        // Find where relationships start (first line with ||, }|, etc.)
        const relStartIndex = sanitized.search(/^\s*[A-Z_][A-Z0-9_]*\s+[|}o]+--/m);
        if (relStartIndex > 0) {
            sanitized = sanitized.substring(0, relStartIndex) + stubs + '\n' + sanitized.substring(relStartIndex);
        } else {
            sanitized = sanitized + '\n' + stubs;
        }
        fixes.push(`Added stub definitions for undefined entities: ${undefinedEntities.join(', ')}`);
    }

    return sanitized;
}

/**
 * Validate Mermaid code and attempt auto-fix if invalid
 * Returns the validation result with sanitized code if fixes were applied
 */
export async function validateAndFixMermaid(code: string): Promise<ValidationResult> {
    // First, apply sanitization
    const sanitizeResult = sanitizeMermaidCode(code);
    let currentCode = sanitizeResult.code;
    const allFixes = [...sanitizeResult.fixesApplied];

    // Try to parse the sanitized code
    try {
        // Use mermaid.parse to validate without rendering
        // Do NOT use suppressErrors - we need the actual error message if it fails
        const parseResult = await mermaid.parse(currentCode);

        if (parseResult) {
            return {
                valid: true,
                sanitizedCode: currentCode,
                fixesApplied: allFixes,
            };
        }
    } catch (error) {
        // Parse failed, try additional fixes based on the error
        const errorMessage = error instanceof Error ? error.message : String(error);

        // Apply error-specific fixes
        const errorFixResult = applyErrorSpecificFixes(currentCode, errorMessage);
        if (errorFixResult.wasFixed) {
            currentCode = errorFixResult.code;
            allFixes.push(...errorFixResult.fixesApplied);

            // Try parsing again after error-specific fixes
            try {
                const retryResult = await mermaid.parse(currentCode);
                if (retryResult) {
                    return {
                        valid: true,
                        sanitizedCode: currentCode,
                        fixesApplied: allFixes,
                    };
                }
            } catch {
                // Still failed, continue to return invalid
            }
        }

        return {
            valid: false,
            error: simplifyErrorMessage(errorMessage),
            sanitizedCode: currentCode,
            fixesApplied: allFixes,
        };
    }

    // Fallback: parse returned falsy without throwing - try once more to get an error
    try {
        await mermaid.parse(currentCode);
    } catch (fallbackError) {
        const msg = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
        return {
            valid: false,
            error: simplifyErrorMessage(msg),
            sanitizedCode: currentCode,
            fixesApplied: allFixes,
        };
    }

    return {
        valid: false,
        error: 'Unknown parsing error',
        sanitizedCode: currentCode,
        fixesApplied: allFixes,
    };
}

/**
 * Apply fixes based on specific error messages
 */
function applyErrorSpecificFixes(code: string, errorMessage: string): SanitizeResult {
    const fixes: string[] = [];
    let fixed = code;

    // "Expecting" errors often indicate syntax issues
    if (errorMessage.includes('Expecting')) {
        // Try removing problematic characters
        const beforeFix = fixed;

        // Remove Unicode special characters that might cause issues
        fixed = fixed.replace(/[\u2018\u2019]/g, "'"); // Smart quotes
        fixed = fixed.replace(/[\u201C\u201D]/g, '"'); // Smart double quotes
        fixed = fixed.replace(/[\u2013\u2014]/g, '-'); // En/em dashes
        fixed = fixed.replace(/\u2026/g, '...'); // Ellipsis

        if (beforeFix !== fixed) {
            fixes.push('Replaced Unicode special characters');
        }
    }

    // "Lexical error" often means invalid characters
    if (errorMessage.includes('Lexical error') || errorMessage.includes('lexical')) {
        const beforeFix = fixed;
        // Remove any non-printable characters except newlines and tabs
        fixed = fixed.replace(/[^\x20-\x7E\n\t]/g, '');
        if (beforeFix !== fixed) {
            fixes.push('Removed non-printable characters');
        }
    }

    // "Parse error" for specific syntax
    if (errorMessage.includes('Parse error')) {
        // Try to fix common structural issues
        const lines = fixed.split('\n');
        const cleanedLines = lines.filter(line => {
            // Remove lines that are just special characters or malformed
            const trimmed = line.trim();
            if (trimmed && !/^[\s\-=<>|]+$/.test(trimmed)) {
                return true;
            }
            return trimmed === '';
        });

        if (cleanedLines.length !== lines.length) {
            fixed = cleanedLines.join('\n');
            fixes.push('Removed malformed lines');
        }
    }

    return {
        code: fixed,
        wasFixed: fixes.length > 0,
        fixesApplied: fixes,
    };
}

/**
 * Simplify Mermaid error messages for user display
 */
function simplifyErrorMessage(error: string): string {
    // Remove line/column references that might be confusing
    let simplified = error
        .replace(/at line \d+, column \d+/gi, '')
        .replace(/line \d+:\d+/gi, '')
        .replace(/Syntax error in graph/i, 'Syntax error')
        .replace(/Lexical error on line \d+\./gi, 'Invalid characters in diagram')
        .trim();

    // Truncate very long error messages
    if (simplified.length > 150) {
        simplified = simplified.substring(0, 147) + '...';
    }

    // If empty after cleanup, provide a generic message
    if (!simplified) {
        simplified = 'Invalid diagram syntax';
    }

    return simplified;
}

/**
 * Quick check if code looks like it might be valid Mermaid
 * Used for fast-fail before expensive parsing
 */
export function looksLikeMermaid(code: string): boolean {
    if (!code || typeof code !== 'string') {
        return false;
    }

    const trimmed = code.trim();
    if (trimmed.length < 5) {
        return false;
    }

    // Check for diagram type declaration
    const hasDiagramType = DIAGRAM_TYPES.some(type => {
        const pattern = new RegExp(`^\\s*${type}`, 'im');
        return pattern.test(trimmed);
    });

    if (hasDiagramType) {
        return true;
    }

    // Check for common Mermaid patterns even without declaration
    const mermaidPatterns = [
        /-->/,              // Flowchart arrows
        /->>|-->>|-\)\)/,   // Sequence diagram arrows (escaped parentheses)
        /\[\*\]/,           // State diagram start/end
        /\w+\[.+\]/,        // Node with label
        /participant\s/i,   // Sequence diagram
        /class\s+\w+/i,     // Class diagram
    ];

    return mermaidPatterns.some(pattern => pattern.test(trimmed));
}
